import { JwtTokens } from '../models/jwt-tokens';
import { HEADER_SKIP_REFRESH_TOKEN, JWT_SERVICE_TOKEN } from './jwt-injection.config';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, catchError, filter, finalize, first, switchMap, take, throwError } from 'rxjs';

export const JwtInterceptorFn: HttpInterceptorFn = (request, next) => {
  const jwtService = inject(JWT_SERVICE_TOKEN);
  const jwtHelperService = new JwtHelperService();

  let isRefreshingToken = false;
  const tokenSubject = new BehaviorSubject<JwtTokens | null>(null);

  const logoutUser = (error?: any): Observable<any> => {
    jwtService.logout();
    return throwError(() => (error ? error : 'LOGOUT')) as any;
  };

  const addToken = (token: string): HttpRequest<any> => {
    const hasXAuthTypeJwt = request.headers.get('x-auth-type') === 'jwt';
    const otherHeaders = hasXAuthTypeJwt
      ? {
          'x-auth-token': token,
          'x-auth-type': 'jwt',
        }
      : {};
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}`, ...otherHeaders } as any,
    });
  };

  const handle401Error = (
    request: HttpRequest<any>,
    next: HttpHandlerFn,
    tokensLocal: JwtTokens
  ): Observable<HttpEvent<any>> => {
    if (!isRefreshingToken) {
      isRefreshingToken = true;

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      tokenSubject.next(null);

      return jwtService.refreshToken(tokensLocal).pipe(
        switchMap((newTokens: JwtTokens | undefined) => {
          if (newTokens) {
            tokenSubject.next(newTokens);
            jwtService.saveLocalTokens({
              token: newTokens.token,
              refreshToken: newTokens.refreshToken,
            });
            return next(addToken(newTokens.token));
          }

          // If we don't get a new token, we are in trouble so logout.
          return logoutUser();
        }),
        catchError((error) => {
          const status = error.status;
          // If there is an exception calling 'refreshToken', bad news so logout.
          if (status >= 400 && status < 500) {
            return logoutUser(error);
          }

          return throwError(() => error) as any;
        }),
        finalize(() => {
          isRefreshingToken = false;
        })
      );
    }

    return tokenSubject.asObservable().pipe(
      filter((tokens): tokens is JwtTokens => tokens !== null),
      take(1),
      switchMap((tokens) => {
        return next(addToken(tokens.token));
      })
    );
  };

  return jwtService.getTokens().pipe(
    first(),
    switchMap((tokens: JwtTokens | undefined) => {
      if (
        tokens &&
        !jwtService.blacklistRoutes.some((apiUrl) => {
          return apiUrl instanceof RegExp ? apiUrl.test(request.url) : request.url.indexOf(apiUrl) !== -1;
        })
      ) {
        if (jwtHelperService.isTokenExpired(tokens.token) && !request.headers.has(HEADER_SKIP_REFRESH_TOKEN)) {
          return handle401Error(request, next, tokens);
        }

        return next(addToken(tokens.token)).pipe(
          catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              return handle401Error(request, next, tokens);
            }
            return throwError(() => error);
          })
        );
      }

      return next(request);
    })
  );
};
